import { defineStore } from "pinia";
import { toRaw } from "vue";
import type { Rarity } from "~/data/gacha";
import type { DaycareQueueEntry } from "~/utils/daycare";
import { persistDaycareReserveQueueBackupForCurrentUser } from "~/utils/daycareReserveBackup";
import {
  createDaycareDepositId,
  DAYCARE_HATCH_DAMAGE,
} from "../../../shared/daycare_merge";
import {
  buildDaycareProgressBackup,
  clearDaycareProgressBackup,
  persistDaycareProgressBackup,
} from "~/utils/daycareProgressBackup";
import { clearDaycareReserveQueueBackupForCurrentUser } from "~/utils/daycareReserveBackup";
import { useAuthStore } from "~/stores/useAuthStore";

/** Copies plain (hors arbre réactif) — évite Reflect.set / Proxy en boucle si `entries` est le même tableau réactif que le state. */
function normalizeReserveQueueEntries(
  entries: Iterable<DaycareQueueEntry>,
): DaycareQueueEntry[] {
  const out: DaycareQueueEntry[] = [];
  for (const e of entries) {
    const x = toRaw(e);
    out.push({
      slug: x.slug,
      nameFr: x.nameFr,
      nameEn: x.nameEn,
      rarity: x.rarity,
      isShiny: Boolean(x.isShiny),
      stars: x.stars,
      cosmeticFormSlug: x.cosmeticFormSlug ?? null,
    });
  }
  return out;
}

function reserveQueuesShallowEqual(
  a: DaycareQueueEntry[],
  b: DaycareQueueEntry[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!;
    const y = b[i]!;
    if (
      x.slug !== y.slug ||
      x.isShiny !== y.isShiny ||
      x.stars !== y.stars ||
      x.rarity !== y.rarity ||
      x.nameFr !== y.nameFr ||
      x.nameEn !== y.nameEn ||
      (x.cosmeticFormSlug ?? null) !== (y.cosmeticFormSlug ?? null)
    ) {
      return false;
    }
  }
  return true;
}

export type DaycareSlot = {
  slug: string;
  nameFr: string;
  nameEn: string;
  stars: number;
  rarity: Rarity;
  isShiny: boolean;
  cosmeticFormSlug?: string | null;
  /** Identifiant unique par dépôt (évite fusion de progression entre deux œufs même espèce). */
  depositId?: string;
  damageDealt: number;
  damageRequired: number;
  isShinyResult?: boolean;
  /** Si vrai : après éclosion, tente de redéposer la même espèce (niv.100) sur ce « créneau » logique. Optionnel, défaut off. */
  loopSameSpecies?: boolean;
};

export const BASE_DAYCARE_SLOTS = 5;
export const MAX_EXTRA_DAYCARE_SLOTS = 5;
export const MAX_DAYCARE_SLOTS = BASE_DAYCARE_SLOTS + MAX_EXTRA_DAYCARE_SLOTS; // 10 absolu
export const MAX_DAYCARE_RESERVE = 30;

// Prix des slots supplémentaires (slot 6 → 10)
export const EXTRA_SLOT_COSTS = [50, 150, 300, 500, 1000] as const;

export const DAYCARE_COST = 0;

export const HATCH_DAMAGE: Record<number, number> = { ...DAYCARE_HATCH_DAMAGE };

export const FIVE_STAR_SHINY_CHANCE = 1 / 500;

function loadBool(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

/** Défaut actif : absent du localStorage = remplissage auto des slots libres (comportement historique). */
function loadVacancyFillDefaultTrue(): boolean {
  try {
    const v = localStorage.getItem("pokegrind-daycare-vacancy-fill");
    if (v === null) return true;
    return v === "1";
  } catch {
    return true;
  }
}

export const useDaycareStore = defineStore("daycare", {
  state: () => ({
    /** Révision serveur connue (monotone). */
    serverRevision: 0,
    /** Révision à envoyer au prochain PATCH /daycare (mutations structurelles). */
    pendingRevision: null as number | null,
    slots: [] as DaycareSlot[],
    lastDeposited: [] as DaycareQueueEntry[],
    reserveQueue: [] as DaycareQueueEntry[],
    reserveFillPending: 0,
    autoRefill: loadBool("pokegrind-autorefill"),
    /** Remplissage passif des slots vides depuis l’inventaire (watcher layout `game.vue`). */
    autoVacancyFill: loadVacancyFillDefaultTrue(),
    /**
     * Quand vrai : le watcher « slots libres » du layout ne relance pas applyDaycareInventoryRefill
     * (évite qu’un retrait manuel soit annulé tout de suite par le même candidat).
     */
    passiveVacancyRefillSuppressed: false,
  }),

  getters: {
    maxSlots: (): number => BASE_DAYCARE_SLOTS, // sera overridé dans les composants
    freeSlots(): number {
      return this.maxSlots - this.slots.length;
    },
    isFull(): boolean {
      return this.slots.length >= this.maxSlots;
    },
  },

  actions: {
    setServerRevision(revision: number) {
      this.serverRevision = Math.max(0, Math.floor(Number(revision) || 0));
    },

    clearPendingRevision() {
      this.pendingRevision = null;
    },

    /** Dépôt, file, éclosion, toggles — pas les ticks de dégâts. */
    noteDaycareMutation() {
      const base = this.pendingRevision ?? this.serverRevision;
      this.pendingRevision = base + 1;
    },

    /** Révision pour PATCH : mutation en attente ou progression des dégâts (sync périodique). */
    revisionForSave(): number {
      if (this.pendingRevision !== null) return this.pendingRevision;
      return this.serverRevision + 1;
    },

    hasSlug(slug: string, isShiny?: boolean): boolean {
      if (isShiny === undefined) return this.slots.some((s) => s.slug === slug);
      return this.slots.some((s) => s.slug === slug && s.isShiny === isShiny);
    },

    deposit(
      pokemon: {
        slug: string;
        nameFr: string;
        nameEn: string;
        stars: number;
        rarity: Rarity;
        isShiny?: boolean;
        cosmeticFormSlug?: string | null;
        loopSameSpecies?: boolean;
      },
      maxSlots?: number,
    ): boolean {
      this.passiveVacancyRefillSuppressed = false;
      const limit = maxSlots ?? BASE_DAYCARE_SLOTS;
      if (this.slots.length >= limit) return false;
      if (this.hasSlug(pokemon.slug, pokemon.isShiny)) return false;
      const starTier = Math.max(
        1,
        Math.min(5, Math.floor(Number(pokemon.stars) || 1)),
      );
      const dmgRequired = HATCH_DAMAGE[starTier] ?? HATCH_DAMAGE[5]!;
      const row: DaycareSlot = {
        slug: pokemon.slug,
        nameFr: pokemon.nameFr,
        nameEn: pokemon.nameEn,
        stars: starTier,
        rarity: pokemon.rarity,
        isShiny: pokemon.isShiny ?? false,
        cosmeticFormSlug: pokemon.cosmeticFormSlug ?? null,
        depositId: createDaycareDepositId(),
        damageDealt: 0,
        damageRequired: dmgRequired,
      };
      // Par défaut : garder la même espèce après éclosion tant que le shiny 5★ n’est pas atteint (désactivable par slot).
      if (pokemon.loopSameSpecies !== false) row.loopSameSpecies = true;
      this.slots.push(row);
      this.noteDaycareMutation();
      this.flushDaycareProgressBackup();
      return true;
    },

    setSlotLoopSame(index: number, value: boolean) {
      if (index < 0 || index >= this.slots.length) return;
      const slot = this.slots[index]!;
      slot.loopSameSpecies = value;
      this.noteDaycareMutation();
    },

    remove(index: number) {
      if (index >= 0 && index < this.slots.length) {
        this.slots.splice(index, 1);
        this.noteDaycareMutation();
        if (this.slots.length === 0) this.flushDaycareProgressBackup();
      }
    },

    addDamage(amount: number) {
      for (const slot of this.slots) {
        const req = Math.max(1, Number(slot.damageRequired) || 1);
        const prev = Math.max(0, Number(slot.damageDealt) || 0);
        if (prev >= req) continue;
        slot.damageDealt = Math.min(req, prev + amount);
        // RNG shiny uniquement serveur (POST /daycare/hatch) — plus de isShinyResult client.
      }
    },

    collectHatched(): { slot: DaycareSlot; isShiny: boolean }[] {
      // Legacy local collect — préférer hatchDaycareOnServer().
      const hatched: { slot: DaycareSlot; isShiny: boolean }[] = [];
      const remaining: DaycareSlot[] = [];
      for (const slot of this.slots) {
        if (slot.damageDealt >= slot.damageRequired) {
          hatched.push({ slot: { ...slot }, isShiny: false });
        } else {
          remaining.push(slot);
        }
      }
      const nextLastDeposited = hatched.map(({ slot }) => ({
        slug: slot.slug,
        nameFr: slot.nameFr,
        nameEn: slot.nameEn,
        rarity: slot.rarity,
        isShiny: slot.isShiny,
        stars: slot.stars,
      }));
      this.lastDeposited.splice(
        0,
        this.lastDeposited.length,
        ...nextLastDeposited,
      );
      this.slots.splice(0, this.slots.length, ...remaining);
      this.noteDaycareMutation();
      this.flushDaycareProgressBackup();
      return hatched;
    },

    /** Récupère un seul œuf prêt (index stable avant suppression). */
    collectHatchedAtIndex(
      index: number,
    ): { slot: DaycareSlot; isShiny: boolean } | null {
      if (index < 0 || index >= this.slots.length) return null;
      const slot = this.slots[index]!;
      if (slot.damageDealt < slot.damageRequired) return null;
      const snapshot: DaycareSlot = { ...slot };
      this.slots.splice(index, 1);
      this.lastDeposited.splice(0, this.lastDeposited.length, {
        slug: snapshot.slug,
        nameFr: snapshot.nameFr,
        nameEn: snapshot.nameEn,
        rarity: snapshot.rarity,
        isShiny: snapshot.isShiny,
        stars: snapshot.stars,
      });
      this.noteDaycareMutation();
      this.flushDaycareProgressBackup();
      return { slot: snapshot, isShiny: false };
    },

    /** Met à jour (ou efface) le backup localStorage après changement pension. */
    flushDaycareProgressBackup() {
      const userId = useAuthStore().user?.id;
      if (!userId) return;
      const cleared =
        this.slots.length === 0 &&
        this.lastDeposited.length === 0 &&
        this.reserveQueue.length === 0 &&
        this.reserveFillPending === 0;
      if (cleared) {
        clearDaycareProgressBackup(userId);
        clearDaycareReserveQueueBackupForCurrentUser();
        return;
      }
      persistDaycareProgressBackup(
        userId,
        buildDaycareProgressBackup({
          slots: this.slots,
          lastDeposited: this.lastDeposited,
          reserveQueue: this.reserveQueue,
          reserveFillPending: this.reserveFillPending,
        }),
      );
    },

    setAutoRefill(value: boolean) {
      if (this.autoRefill !== value) this.noteDaycareMutation();
      this.autoRefill = value;
      try {
        localStorage.setItem("pokegrind-autorefill", value ? "1" : "0");
      } catch {
        /* ignore */
      }
    },

    setAutoVacancyFill(value: boolean) {
      if (this.autoVacancyFill !== value) this.noteDaycareMutation();
      this.autoVacancyFill = value;
      try {
        localStorage.setItem(
          "pokegrind-daycare-vacancy-fill",
          value ? "1" : "0",
        );
      } catch {
        /* ignore */
      }
    },

    enqueueReserve(entries: DaycareQueueEntry[]) {
      const existing = new Set(
        this.reserveQueue.map(
          (entry) => `${entry.slug}:${entry.isShiny ? 1 : 0}`,
        ),
      );
      let added = 0;
      for (const entry of entries) {
        if (this.reserveQueue.length >= MAX_DAYCARE_RESERVE) break;
        const raw = toRaw(entry);
        const key = `${raw.slug}:${raw.isShiny ? 1 : 0}`;
        if (existing.has(key)) continue;
        this.reserveQueue.push({
          slug: raw.slug,
          nameFr: raw.nameFr,
          nameEn: raw.nameEn,
          rarity: raw.rarity,
          isShiny: Boolean(raw.isShiny),
          stars: raw.stars,
          cosmeticFormSlug: raw.cosmeticFormSlug ?? null,
        });
        existing.add(key);
        added++;
      }
      persistDaycareReserveQueueBackupForCurrentUser(this.reserveQueue);
      this.noteDaycareMutation();
      this.flushDaycareProgressBackup();
      return added;
    },

    dequeueReserveBySlug(slug: string, isShiny = false) {
      const index = this.reserveQueue.findIndex(
        (entry) => entry.slug === slug && entry.isShiny === isShiny,
      );
      if (index >= 0) {
        this.reserveQueue.splice(index, 1);
        this.noteDaycareMutation();
      }
      persistDaycareReserveQueueBackupForCurrentUser(this.reserveQueue);
      this.flushDaycareProgressBackup();
    },

    setReserveQueue(entries: DaycareQueueEntry[]) {
      const next = normalizeReserveQueueEntries(
        Array.isArray(entries) ? entries : [],
      );
      const cur = normalizeReserveQueueEntries(toRaw(this.reserveQueue));
      if (reserveQueuesShallowEqual(cur, next)) return;
      // Mutation en place plutôt que `this.reserveQueue = next` : avec Pinia 3 + Vue 3.5,
      // les propriétés de state sont des `ObjectRefImpl`, et l'assignation directe d'un
      // tableau peut déclencher une récursion `set → set value → set` (cf. enquête bug
      // « too much recursion » mai 2026). `splice` mute le tableau réactif sans réécrire
      // la propriété sur le proxy.
      this.reserveQueue.splice(0, this.reserveQueue.length, ...next);
      persistDaycareReserveQueueBackupForCurrentUser(this.reserveQueue);
      this.noteDaycareMutation();
      this.flushDaycareProgressBackup();
    },

    /** Voir commentaire `setReserveQueue` : mutation en place pour éviter la récursion Pinia/Vue. */
    setSlots(next: DaycareSlot[]) {
      this.slots.splice(0, this.slots.length, ...next);
    },

    /** Voir commentaire `setReserveQueue` : mutation en place pour éviter la récursion Pinia/Vue. */
    setLastDeposited(next: DaycareQueueEntry[]) {
      this.lastDeposited.splice(0, this.lastDeposited.length, ...next);
    },

    addReserveFillPending(count: number) {
      if (count <= 0) return;
      this.reserveFillPending = Math.max(
        0,
        this.reserveFillPending + Math.floor(count),
      );
      this.noteDaycareMutation();
    },

    consumeReserveFillPending(count: number) {
      if (count <= 0) return;
      this.reserveFillPending = Math.max(
        0,
        this.reserveFillPending - Math.floor(count),
      );
      this.noteDaycareMutation();
    },

    setReserveFillPending(count: number) {
      const next = Math.max(0, Math.floor(count));
      if (next !== this.reserveFillPending) this.noteDaycareMutation();
      this.reserveFillPending = next;
    },

    setPassiveVacancyRefillSuppressed(value: boolean) {
      this.passiveVacancyRefillSuppressed = value;
    },
  },
});
